import React, { useRef } from 'react';
import { cardStyles, PlacementSliders } from './Controls';
import styles from './panelStyles';

export const MAX_LOGOS = 5;

/**
 * LogoPanel – upload PNG/SVG logos and position them on the garment.
 *
 * Props:
 *   logos    – { id, name, dataUrl, x, y, scale, rotate }[]
 *   onAdd    – (logo) => void
 *   onRemove – (id) => void
 *   onUpdate – (id, patch) => void
 */
export default function LogoPanel({ logos, onAdd, onRemove, onUpdate }) {
    const inputRef = useRef(null);
    const canAdd = logos.length < MAX_LOGOS;

    const handleFiles = (e) => {
        const files = Array.from(e.target.files).slice(0, MAX_LOGOS - logos.length);
        e.target.value = ''; // allow re-selecting the same file
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (ev) => onAdd({
                id: `logo-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                name: file.name,
                dataUrl: ev.target.result,
                x: 825, // centre of the 1650x1550 artboard
                y: 775,
                scale: 1,
                rotate: 0,
            });
            reader.readAsDataURL(file);
        });
    };

    return (
        <div style={styles.root}>
            <div>
                <div style={styles.sectionLabel}>Logos ({logos.length} / {MAX_LOGOS})</div>
                <button
                    style={cardStyles.addBtn(canAdd)}
                    disabled={!canAdd}
                    onClick={() => inputRef.current.click()}
                    title={canAdd ? 'Upload a PNG or SVG logo' : `Maximum ${MAX_LOGOS} logos reached`}
                >
                    + Add Logo
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".png,.svg,image/png,image/svg+xml"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFiles}
                />
            </div>

            {logos.length === 0 && (
                <p style={cardStyles.hint}>
                    Upload up to {MAX_LOGOS} PNG or SVG logos.<br />
                    Use the sliders to position and size each one.
                </p>
            )}

            {logos.map((logo) => (
                <div key={logo.id} style={cardStyles.card}>
                    <div style={cardStyles.cardHeader}>
                        <img
                            src={logo.dataUrl}
                            alt={logo.name}
                            style={{
                                width: '36px',
                                height: '36px',
                                objectFit: 'contain',
                                borderRadius: '3px',
                                background: '#111',
                                border: '1px solid #333',
                                flexShrink: 0,
                            }}
                        />
                        <span style={cardStyles.cardTitle} title={logo.name}>{logo.name}</span>
                        <button style={cardStyles.removeBtn} onClick={() => onRemove(logo.id)} title="Remove logo">
                            ✕
                        </button>
                    </div>

                    <div style={cardStyles.divider} />

                    <PlacementSliders
                        item={logo}
                        scaleRange={{ min: 0.05, max: 4, step: 0.01 }}
                        onUpdate={(patch) => onUpdate(logo.id, patch)}
                    />
                </div>
            ))}
        </div>
    );
}
